// pages/linktree/PublicLinktree.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Button,
    CircularProgress,
    Stack,
    Alert,
} from '@mui/material';
import {
    Link as LinkIcon,
    Facebook as FacebookIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    YouTube as YouTubeIcon,
    WhatsApp as WhatsAppIcon,
    Telegram as TelegramIcon,
    MusicNote as MusicNoteIcon,
    Lock as LockIcon,
    Pinterest as PinterestIcon,
    LinkedIn as LinkedInIcon,
    Reddit as RedditIcon,
    GitHub as GitHubIcon,
    Language as LanguageIcon,
    Moped as MopedIcon,
} from '@mui/icons-material';
import { makeRequest } from '../../../assets/functions_helper';
import { initGoogleAnalytics } from '../../../assets/analytics_helper';

// Mapeo de iconos (debe coincidir con el usado en el editor)
const iconMap = {
    link: LinkIcon,
    facebook: FacebookIcon,
    instagram: InstagramIcon,
    twitter: TwitterIcon,
    youtube: YouTubeIcon,
    whatsapp: WhatsAppIcon,
    telegram: TelegramIcon,
    tiktok: MusicNoteIcon,
    pinterest: PinterestIcon,
    linkedin: LinkedInIcon,
    reddit: RedditIcon,
    github: GitHubIcon,
    onlyfans: LockIcon,
    web: LanguageIcon,
    delivery: MopedIcon,
};

// Función para obtener color de texto contrastante (blanco para fondos oscuros, negro para claros)
const getContrastText = (hexcolor) => {
    if (!hexcolor) return '#1E1E1E';
    // Eliminar # si existe
    const color = hexcolor.startsWith('#') ? hexcolor.slice(1) : hexcolor;
    if (color.length === 3) {
        // Convertir RGB corto a completo
        const r = parseInt(color[0] + color[0], 16);
        const g = parseInt(color[1] + color[1], 16);
        const b = parseInt(color[2] + color[2], 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#1E1E1E' : '#FFFFFF';
    } else if (color.length === 6) {
        const r = parseInt(color.slice(0, 2), 16);
        const g = parseInt(color.slice(2, 4), 16);
        const b = parseInt(color.slice(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.5 ? '#1E1E1E' : '#FFFFFF';
    }
    return '#1E1E1E';
};

// Función auxiliar para asegurar que la URL tenga protocolo
const ensureAbsoluteUrl = (url) => {
    if (!url) return url;
    // Si ya comienza con http://, https:// o //, la dejamos como está
    if (/^(https?:)?\/\//i.test(url)) {
        return url;
    }
    // Si no, agregamos https:// por defecto
    return `https://${url}`;
};

export default function PublicLinktree() {
    const { publicId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);

    useEffect(() => {
        const fetchPublicLinktree = async () => {
            setLoading(true);
            try {
                const linktreePromise = makeRequest(
                    'GET',
                    `restaurant_admin/public/link_page/${publicId}`,
                    null,
                    'application/json',
                    null,
                    null
                );

                const gaPromise = makeRequest(
                    'GET',
                    `restaurant_admin/public/measurement_id/${publicId}`,
                    null,
                    'application/json',
                    null,
                    null
                );

                const [linktreeResult, gaResult] = await Promise.allSettled([linktreePromise, gaPromise]);

                if (linktreeResult.status === 'rejected') {
                    throw linktreeResult.reason;
                }

                setData(linktreeResult.value);
                setError(null);

                if (gaResult.status === 'fulfilled' && gaResult.value && gaResult.value.google_analytics_measurement_id) {
                    initGoogleAnalytics(gaResult.value.google_analytics_measurement_id);
                }
            } catch (err) {
                setError('No se pudo cargar la información');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        if (publicId) {
            fetchPublicLinktree();
        }
    }, [publicId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !data) {
        return (
            <Container maxWidth="sm" sx={{ mt: 4 }}>
                <Alert severity="error">{error || 'No se encontró la página'}</Alert>
            </Container>
        );
    }

    const { background_image_url, logo_url, links, restaurant_name } = data;

    return (
        <Box
            sx={{
                minHeight: '100vh',
                backgroundImage: background_image_url ? `url(${background_image_url})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2,
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    maxWidth: 400,
                    width: '100%',
                    borderRadius: '32px',
                    overflow: 'hidden',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    backdropFilter: 'blur(10px)',
                }}
            >
                <Box sx={{ p: 3 }}>
                    {logo_url && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box
                                component="img"
                                src={logo_url}
                                sx={{
                                    width: 140,
                                    height: 140,
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    border: '4px solid white',
                                    boxShadow: '0 8px 20px rgba(0,0,0,0.2)',
                                }}
                            />
                        </Box>
                    )}

                    <Typography variant="h5" align="center" sx={{ fontWeight: 700, mb: 3 }}>
                        {restaurant_name || 'Mi Restaurante'}
                    </Typography>

                    <Stack spacing={2}>
                        {links
                            .filter((link) => link.is_active)
                            .map((link, index) => {
                                const IconComponent = iconMap[link.icon] || LinkIcon;
                                // Determinar color de fondo: usar button_color si existe, sino usar dark_mode
                                const bgColor = link.button_color || (link.dark_mode ? '#1E1E1E' : '#FFFFFF');
                                const textColor = getContrastText(bgColor);
                                // Asegurar que la URL sea absoluta
                                const absoluteUrl = ensureAbsoluteUrl(link.url);
                                return (
                                    <Button
                                        key={index}
                                        variant="contained"
                                        fullWidth
                                        startIcon={<IconComponent />}
                                        sx={{
                                            bgcolor: bgColor,
                                            color: textColor,
                                            borderRadius: '40px',
                                            textTransform: 'none',
                                            py: 1.5,
                                            fontSize: '1rem',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                            '&:hover': {
                                                bgcolor: bgColor,
                                                filter: 'brightness(0.95)',
                                            },
                                            justifyContent: 'flex-start',
                                            pl: 3,
                                        }}
                                        href={absoluteUrl}
                                        target="_blank"
                                        rel="noopener"
                                    >
                                        {link.title || 'Enlace'}
                                    </Button>
                                );
                            })}
                    </Stack>
                </Box>
            </Paper>
        </Box>
    );
}